import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  savingsVaultCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { mutateIdempotent } from "@/core/api/mutate-idempotent";
import { logger } from "@/core/utils/func/logger";
import { savingsEndpoints } from "@/features/savings/api/endpoints";
import type { SavingsVaultDto } from "@/features/savings/api/dto";

/**
 * Ferme un coffre vide (`POST /SavingsVaults/{id}/close`) — soldes comptable
 * et en attente nuls, sinon 422. Rejouer sur un coffre déjà clos est sans
 * effet. Idempotency-Key OBLIGATOIRE (guide §4.7).
 */
export const useCloseSavingsVault = (vaultId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["savings", "savingsVaults", "close", vaultId],
    mutationFn: () =>
      mutateIdempotent<SavingsVaultDto>({
        scopeId: `savings.savingsVaults.close.${vaultId}`,
        url: savingsEndpoints.savingsVaults.close(vaultId),
        body: {},
      }),
    onSuccess: () =>
      invalidateAfterMutation(queryClient, savingsVaultCacheKeys(vaultId)),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCloseSavingsVault failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "savings" }
      );
    },
  });
};
